import React, { useEffect, useState } from "react";

import { CButton, CInput, CPagination, CSpinner } from "@coreui/react";
import {
  FaArrowDown,
  FaArrowUp,
  FaClock,
  FaClipboardList,
  FaListOl,
  FaPencilAlt,
  FaPlusCircle,
  FaRandom,
  FaSearch,
  FaTrash
} from "react-icons/fa";
import { toastr } from "react-redux-toastr";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";

import FormularioQuestionBank, { QuestionForm } from "./FormularioQuestionBank";
import FormularioRichContent from "./FormularioRichContent";
import {
  TEMPO_ILIMITADO,
  TEMPO_MAXIMO,
  TEMPO_MINIMO,
  TEMPO_PADRAO,
  isTempoIlimitado,
  letraDaOpcao,
  tempoDoFormulario
} from "./opcoes";
import URL from "../../config/api";
import axios from "../../config/axios";

import "./formulario-editor.css";

const auth = token => ({ headers: { authorization: `Bearer ${token}` } });

function SelectedQuestion({
  question,
  index,
  total,
  onMove,
  onEdit,
  onRemove,
  readOnly
}) {
  return (
    <article className="formulario-editor-question">
      <div className="formulario-editor-question__toolbar">
        <strong>{index + 1}ª pergunta</strong>
        {!readOnly && (
          <>
            <button
              type="button"
              disabled={index === total - 1}
              onClick={() => onMove(index, 1)}
              title="Mover para baixo"
            >
              <FaArrowDown />
            </button>
            <button
              type="button"
              disabled={index === 0}
              onClick={() => onMove(index, -1)}
              title="Mover para cima"
            >
              <FaArrowUp />
            </button>
          </>
        )}
      </div>
      <div className="formulario-editor-question__card">
        <div className="formulario-editor-question__meta">
          <span>Escolha única</span>
          {!readOnly && (
            <span className="formulario-editor-question__actions">
              {question.pode_editar !== false && (
                <button
                  type="button"
                  className="btn btn-link p-0"
                  onClick={() => onEdit(question)}
                  title="Editar pergunta"
                  aria-label="Editar pergunta"
                >
                  <FaPencilAlt />
                </button>
              )}
              <button
                type="button"
                className="btn btn-link text-danger p-0"
                onClick={() => onRemove(question.id)}
              >
                <FaTrash className="mr-1" /> Remover
              </button>
            </span>
          )}
        </div>
        <div className="formulario-editor-question__statement">
          <FormularioRichContent value={question.enunciado} readOnly />
        </div>
        <div className="formulario-editor-question__options">
          {(question.opcoes || []).map((option, optionIndex) => (
            <div
              className="formulario-editor-question__option"
              key={option.id || optionIndex}
            >
              <span>{letraDaOpcao(optionIndex)}</span>
              <FormularioRichContent value={option.texto} readOnly />
            </div>
          ))}
        </div>
      </div>
    </article>
  );
}

export function FormularioEditor({
  formulario,
  token,
  onSaved,
  onCancel,
  onUse,
  onDuplicate,
  duplicating = false,
  readOnly = false,
  showReadOnlyNotice = true
}) {
  const [form, setForm] = useState({
    titulo: formulario?.titulo || "",
    descricao: formulario?.descricao || "",
    tempo_por_pergunta: tempoDoFormulario(formulario?.tempo_por_pergunta),
    ordem_execucao: formulario?.ordem_execucao || "fixa",
    disciplina_id: formulario?.disciplina_id || "",
    perguntas: formulario?.perguntas || []
  });
  const [bankOpen, setBankOpen] = useState(false);
  const [bankCreating, setBankCreating] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [saving, setSaving] = useState(false);
  const semLimite = isTempoIlimitado(form.tempo_por_pergunta);
  // Volta para o último limite digitado ao desligar o "sem limite"
  const [ultimoLimite, setUltimoLimite] = useState(
    semLimite ? TEMPO_PADRAO : form.tempo_por_pergunta
  );

  const alternarSemLimite = ativo => {
    if (ativo) setUltimoLimite(form.tempo_por_pergunta);
    setForm(old => ({
      ...old,
      tempo_por_pergunta: ativo ? TEMPO_ILIMITADO : ultimoLimite
    }));
  };

  const toggleQuestion = question => {
    if (readOnly) return;
    setForm(old => ({
      ...old,
      perguntas: old.perguntas.some(item => item.id === question.id)
        ? old.perguntas.filter(item => item.id !== question.id)
        : [...old.perguntas, question]
    }));
  };

  const moveQuestion = (index, direction) => {
    if (readOnly) return;
    setForm(old => {
      const perguntas = [...old.perguntas];
      const target = index + direction;
      if (target < 0 || target >= perguntas.length) return old;
      [perguntas[index], perguntas[target]] = [
        perguntas[target],
        perguntas[index]
      ];
      return { ...old, perguntas };
    });
  };

  const save = async event => {
    event.preventDefault();
    if (readOnly) return;
    if (!form.perguntas.length) {
      toastr.error("Atenção", "Adicione ao menos uma pergunta ao formulário.");
      return;
    }
    const tempo = Number(form.tempo_por_pergunta);
    if (
      !semLimite &&
      (!Number.isFinite(tempo) || tempo < TEMPO_MINIMO || tempo > TEMPO_MAXIMO)
    ) {
      toastr.error(
        "Atenção",
        `O tempo por pergunta deve ficar entre ${TEMPO_MINIMO} e ${TEMPO_MAXIMO} segundos, ou sem limite.`
      );
      return;
    }
    setSaving(true);
    try {
      const response = await axios({
        method: formulario ? "patch" : "post",
        url: `${URL.api}formulario${formulario ? `/${formulario.id}` : ""}`,
        data: {
          ...form,
          disciplina_id: form.disciplina_id || null,
          visibilidade: "privado",
          perguntas: form.perguntas.map(question => ({ id: question.id }))
        },
        ...auth(token)
      });
      onSaved(response.data.data);
    } catch (error) {
      toastr.error(
        "Erro",
        error.response?.data?.error || "Não foi possível salvar o formulário."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <form
        className={`formulario-editor ${
          readOnly ? "formulario-editor--readonly" : ""
        }`}
        onSubmit={save}
      >
        <section className="formulario-editor__settings">
          {readOnly && showReadOnlyNotice && (
            <div className="formulario-editor__locked-notice" role="alert">
              Este formulário já possui respostas e não pode mais ser editado.
              As configurações e perguntas permanecem disponíveis para consulta.
            </div>
          )}

          <div className="formulario-editor__field">
            <label htmlFor="formulario-title">Nome do formulário</label>
            <input
              id="formulario-title"
              className="formulario-editor__title"
              placeholder="Ex.: Pesquisa de satisfação da turma"
              value={form.titulo}
              onChange={event =>
                setForm({ ...form, titulo: event.target.value })
              }
              disabled={readOnly}
              required
            />
          </div>

          <div className="formulario-editor__field">
            <label htmlFor="formulario-description">Descrição</label>
            <textarea
              id="formulario-description"
              className="formulario-editor__description"
              rows="3"
              placeholder="Explique brevemente o objetivo do formulário (opcional)"
              value={form.descricao}
              onChange={event =>
                setForm({ ...form, descricao: event.target.value })
              }
              disabled={readOnly}
            />
          </div>

          <div className="formulario-editor__configuration-grid">
            <div className="formulario-editor__configuration-card">
              <div className="formulario-editor__configuration-heading">
                <span className="formulario-editor__configuration-icon">
                  <FaClock aria-hidden="true" />
                </span>
                <div>
                  <label htmlFor="formulario-time">Tempo por pergunta</label>
                  <small>
                    {semLimite
                      ? "O aluno responde no próprio ritmo; o tempo de cada pergunta continua sendo registrado."
                      : "O mesmo limite será usado em todo o formulário."}
                  </small>
                </div>
              </div>
              {!semLimite && (
                <div className="formulario-editor__time-input">
                  <input
                    id="formulario-time"
                    type="number"
                    min={TEMPO_MINIMO}
                    max={TEMPO_MAXIMO}
                    value={form.tempo_por_pergunta}
                    onChange={event =>
                      setForm({
                        ...form,
                        tempo_por_pergunta: Number(event.target.value)
                      })
                    }
                    disabled={readOnly}
                  />
                  <span>segundos</span>
                </div>
              )}
              <label className="formulario-editor__unlimited">
                <input
                  type="checkbox"
                  checked={semLimite}
                  onChange={event => alternarSemLimite(event.target.checked)}
                  disabled={readOnly}
                />
                <span>Sem limite de tempo</span>
              </label>
            </div>

            <fieldset
              className="formulario-editor__configuration-card formulario-editor__order"
              disabled={readOnly}
            >
              <div className="formulario-editor__configuration-heading">
                <span className="formulario-editor__configuration-icon">
                  <FaRandom aria-hidden="true" />
                </span>
                <div>
                  <legend>Ordem de apresentação</legend>
                  <small>Defina como cada aluno receberá as perguntas.</small>
                </div>
              </div>
              <div className="formulario-editor__order-options">
                <label
                  className={`formulario-editor__order-option ${
                    form.ordem_execucao === "fixa" ? "is-selected" : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="formulario-question-order"
                    value="fixa"
                    checked={form.ordem_execucao === "fixa"}
                    onChange={() =>
                      setForm({ ...form, ordem_execucao: "fixa" })
                    }
                  />
                  <FaListOl aria-hidden="true" />
                  <span>
                    <strong>Ordem definida</strong>
                    <small>Segue a sequência montada pelo professor.</small>
                  </span>
                </label>
                <label
                  className={`formulario-editor__order-option ${
                    form.ordem_execucao === "aleatoria" ? "is-selected" : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="formulario-question-order"
                    value="aleatoria"
                    checked={form.ordem_execucao === "aleatoria"}
                    onChange={() =>
                      setForm({ ...form, ordem_execucao: "aleatoria" })
                    }
                  />
                  <FaRandom aria-hidden="true" />
                  <span>
                    <strong>Ordem aleatória</strong>
                    <small>Embaralha as perguntas em cada execução.</small>
                  </span>
                </label>
              </div>
            </fieldset>
          </div>
        </section>

        <section className="formulario-editor__questions">
          {form.perguntas.length > 0 && (
            <span className="formulario-editor__question-total">
              {form.perguntas.length} pergunta(s)
            </span>
          )}
          {form.perguntas.map((question, index) => (
            <SelectedQuestion
              key={question.id}
              question={question}
              index={index}
              total={form.perguntas.length}
              onMove={moveQuestion}
              onEdit={setEditingQuestion}
              readOnly={readOnly}
              onRemove={id =>
                setForm(old => ({
                  ...old,
                  perguntas: old.perguntas.filter(item => item.id !== id)
                }))
              }
            />
          ))}
          {!readOnly && (
            <div
              className={`formulario-editor__add ${
                form.perguntas.length ? "" : "formulario-editor__add--empty"
              }`}
            >
              {!form.perguntas.length && (
                <>
                  <span className="formulario-editor__empty-icon">
                    <FaListOl aria-hidden="true" />
                  </span>
                  <strong>Nenhuma pergunta adicionada</strong>
                  <p>
                    Selecione perguntas do banco ou cadastre uma nova para
                    começar.
                  </p>
                </>
              )}
              <CButton
                type="button"
                color="primary"
                onClick={() => setBankOpen(true)}
              >
                Adicionar pergunta <FaPlusCircle className="ml-2" />
              </CButton>
            </div>
          )}
        </section>

        <footer
          className={`formulario-editor__actions ${
            readOnly ? "formulario-editor__actions--readonly" : ""
          }`}
        >
          {!readOnly && (
            <span>
              {form.perguntas.length
                ? `${form.perguntas.length} pergunta(s) no formulário`
                : "Adicione ao menos uma pergunta para salvar"}
            </span>
          )}
          <div>
            <CButton
              type="button"
              color={readOnly ? "primary" : "secondary"}
              variant="outline"
              className={`mr-2 ${readOnly ? "formulario-editor__close" : ""}`}
              onClick={onCancel}
            >
              {readOnly ? "Fechar" : "Cancelar"}
            </CButton>
            {!readOnly && (
              <CButton type="submit" color="primary" disabled={saving}>
                {saving ? <CSpinner size="sm" /> : "Salvar formulário"}
              </CButton>
            )}
            {readOnly && onDuplicate && (
              <CButton
                type="button"
                color="primary"
                className={onUse ? "mr-2" : ""}
                disabled={duplicating}
                onClick={onDuplicate}
              >
                {duplicating ? <CSpinner size="sm" /> : "Duplicar e editar"}
              </CButton>
            )}
            {readOnly && onUse && (
              <CButton type="button" color="primary" onClick={onUse}>
                Adicionar à atividade
              </CButton>
            )}
          </div>
        </footer>
      </form>

      <Modal
        isOpen={!readOnly && bankOpen}
        toggle={() => {
          setBankOpen(false);
          setBankCreating(false);
        }}
        backdrop="static"
        size="xl"
        className="formulario-bank-modal"
      >
        <ModalHeader
          toggle={() => {
            setBankOpen(false);
            setBankCreating(false);
          }}
        >
          Banco de perguntas
        </ModalHeader>
        <ModalBody className="p-0">
          <FormularioQuestionBank
            token={token}
            selectedIds={form.perguntas.map(question => question.id)}
            onToggle={toggleQuestion}
            allowSelection
            onCreatingChange={setBankCreating}
          />
        </ModalBody>
        {!bankCreating && (
          <ModalFooter className="formulario-bank-modal__footer">
            <span className="formulario-bank-modal__selection">
              <strong>{form.perguntas.length}</strong>
              {form.perguntas.length === 1
                ? " pergunta selecionada"
                : " perguntas selecionadas"}
            </span>
            <CButton color="primary" onClick={() => setBankOpen(false)}>
              Adicionar selecionadas
            </CButton>
          </ModalFooter>
        )}
      </Modal>

      <Modal
        isOpen={!readOnly && Boolean(editingQuestion)}
        toggle={() => setEditingQuestion(null)}
        backdrop="static"
        size="xl"
        className="formulario-question-edit-modal"
      >
        <ModalHeader toggle={() => setEditingQuestion(null)}>
          Editar pergunta
        </ModalHeader>
        <ModalBody className="p-0">
          {editingQuestion && (
            <QuestionForm
              token={token}
              question={editingQuestion}
              onCancel={() => setEditingQuestion(null)}
              onSaved={updatedQuestion => {
                setForm(old => ({
                  ...old,
                  perguntas: old.perguntas.map(question =>
                    question.id === updatedQuestion.id
                      ? updatedQuestion
                      : question
                  )
                }));
                setEditingQuestion(null);
              }}
            />
          )}
        </ModalBody>
      </Modal>
    </>
  );
}

export default function FormularioPicker({
  token,
  onSelect,
  onClose,
  selectedId
}) {
  const [formularios, setFormularios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedFormularioId, setSelectedFormularioId] = useState(
    selectedId || null
  );
  const [opening, setOpening] = useState(false);
  const [openingFormularioId, setOpeningFormularioId] = useState(null);
  const [editorOrigin, setEditorOrigin] = useState(null);
  const [duplicating, setDuplicating] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);

    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    axios
      .get(`${URL.api}formulario/mine`, {
        ...auth(token),
        params: {
          page,
          per_page: 8,
          search: debouncedSearch || undefined
        }
      })
      .then(response => {
        if (!active) return;
        const result = response.data?.data || {};
        setFormularios(result.data || []);
        setLastPage(result.last_page || 1);
        setTotal(result.total || 0);
      })
      .catch(
        () =>
          active &&
          toastr.error("Erro", "Não foi possível carregar os formulários.")
      )
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, [token, page, debouncedSearch]);

  const addSelectedFormulario = async () => {
    if (!selectedFormularioId || opening) return;
    setOpening(true);
    try {
      const response = await axios.get(
        `${URL.api}formulario/${selectedFormularioId}`,
        auth(token)
      );
      onSelect(response.data?.data);
    } catch (error) {
      toastr.error(
        "Erro",
        error.response?.data?.error ||
          "Não foi possível adicionar o formulário à atividade."
      );
    } finally {
      setOpening(false);
    }
  };

  const openFormularioForEditing = async formularioId => {
    if (openingFormularioId) return;
    setOpeningFormularioId(formularioId);
    try {
      const response = await axios.get(
        `${URL.api}formulario/${formularioId}`,
        auth(token)
      );
      setEditorOrigin("edit");
      setEditing(response.data?.data || null);
    } catch (error) {
      toastr.error(
        "Erro",
        error.response?.data?.error || "Não foi possível abrir o formulário."
      );
    } finally {
      setOpeningFormularioId(null);
    }
  };

  const duplicateAndEdit = async () => {
    if (!editing?.id || duplicating) return;
    setDuplicating(true);
    try {
      const response = await axios.post(
        `${URL.api}formulario/${editing.id}/duplicate`,
        {},
        auth(token)
      );
      const duplicatedFormulario = response.data?.data;
      const duplicatedFormularioForList = {
        ...duplicatedFormulario,
        total_perguntas: duplicatedFormulario.perguntas?.length || 0
      };
      setFormularios(old => [
        duplicatedFormularioForList,
        ...old.filter(item => item.id !== duplicatedFormulario.id)
      ]);
      setSelectedFormularioId(duplicatedFormulario.id);
      setEditorOrigin("edit");
      setEditing(duplicatedFormulario);
      setTotal(old => old + 1);
      toastr.success(
        "Sucesso",
        "Formulário duplicado. Agora você pode editá-lo."
      );
    } catch (error) {
      toastr.error(
        "Erro",
        error.response?.data?.error || "Não foi possível duplicar o formulário."
      );
    } finally {
      setDuplicating(false);
    }
  };

  if (editing !== null)
    return (
      <FormularioEditor
        key={editing?.id || "new-formulario"}
        formulario={editing || null}
        token={token}
        readOnly={editing?.pode_editar === false}
        duplicating={duplicating}
        onDuplicate={
          editing?.pode_editar === false ? duplicateAndEdit : undefined
        }
        onCancel={() => {
          setEditing(null);
          setEditorOrigin(null);
        }}
        onSaved={savedFormulario => {
          const savedFormularioForList = {
            ...savedFormulario,
            total_perguntas: savedFormulario.perguntas?.length || 0
          };
          setFormularios(old => [
            savedFormularioForList,
            ...old.filter(item => item.id !== savedFormulario.id)
          ]);
          setSelectedFormularioId(savedFormulario.id);
          if (editorOrigin === "create") onSelect(savedFormulario);
          setEditing(null);
          setEditorOrigin(null);
        }}
      />
    );

  return (
    <div className="formulario-picker">
      <header className="formulario-picker__list-header">
        <h3>Formulários disponíveis</h3>
        <div className="formulario-picker__list-actions">
          <div className="formulario-picker__search">
            <FaSearch aria-hidden="true" />
            <CInput
              id="formulario-picker-search"
              value={search}
              placeholder="Pesquisar"
              aria-label="Pesquisar formulário"
              onChange={event => setSearch(event.target.value)}
            />
          </div>
          <CButton
            className="formulario-picker__new"
            color="primary"
            onClick={() => {
              setEditorOrigin("create");
              setEditing(false);
            }}
          >
            <FaPlusCircle className="mr-2" aria-hidden="true" /> Novo formulário
          </CButton>
        </div>
      </header>

      <main className="formulario-picker__content">
        {loading ? (
          <div className="formulario-picker__empty">
            <CSpinner />
            <p>Carregando seus formulários...</p>
          </div>
        ) : formularios.length ? (
          <div className="formulario-picker__grid">
            {formularios.map(item => {
              const selected = Number(selectedFormularioId) === Number(item.id);
              const isOpening = Number(openingFormularioId) === Number(item.id);

              return (
                <article
                  key={item.id}
                  className={`formulario-picker-card ${
                    selected ? "is-selected" : ""
                  }`}
                >
                  <button
                    type="button"
                    className="formulario-picker-card__select"
                    aria-pressed={selected}
                    onClick={() => setSelectedFormularioId(item.id)}
                  >
                    <strong title={item.titulo}>{item.titulo}</strong>
                    <p>{item.descricao || "Sem descrição."}</p>
                    <div className="formulario-picker-card__details">
                      <span>
                        <FaListOl aria-hidden="true" />{" "}
                        {item.total_perguntas || 0} pergunta(s)
                      </span>
                      <span>
                        <FaClock aria-hidden="true" />
                        {isTempoIlimitado(item.tempo_por_pergunta)
                          ? "Sem limite de tempo"
                          : `${tempoDoFormulario(
                              item.tempo_por_pergunta
                            )}s por pergunta`}
                      </span>
                    </div>
                  </button>
                  <button
                    type="button"
                    className="formulario-picker-card__edit"
                    aria-label={`Editar formulário ${item.titulo}`}
                    title="Editar formulário"
                    disabled={Boolean(openingFormularioId)}
                    onClick={() => openFormularioForEditing(item.id)}
                  >
                    {isOpening ? <CSpinner size="sm" /> : <FaPencilAlt />}
                  </button>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="formulario-picker__empty">
            <span className="formulario-picker__empty-icon">
              <FaClipboardList aria-hidden="true" />
            </span>
            <strong>
              {debouncedSearch
                ? "Nenhum formulário corresponde à pesquisa"
                : "Você ainda não criou nenhum formulário"}
            </strong>
            <p>
              {debouncedSearch
                ? "Tente pesquisar usando outro nome ou descrição."
                : "Crie um formulário para adicioná-lo a esta atividade."}
            </p>
          </div>
        )}

        {lastPage > 1 && (
          <div className="formulario-picker__pagination">
            <CPagination
              activePage={page}
              pages={lastPage}
              onActivePageChange={setPage}
            />
          </div>
        )}
      </main>

      <footer className="formulario-picker__footer">
        <span>
          <strong>{total}</strong>{" "}
          {total === 1 ? "formulário disponível" : "formulários disponíveis"}
        </span>
        <div>
          <CButton color="secondary" className="mr-2" onClick={onClose}>
            Cancelar
          </CButton>
          <CButton
            color="primary"
            disabled={!selectedFormularioId || opening}
            onClick={addSelectedFormulario}
          >
            {opening ? <CSpinner size="sm" /> : "Continuar"}
          </CButton>
        </div>
      </footer>
    </div>
  );
}
